import { ApiError } from './api-error';

/**
 * The fetch core every app's API client is built on: URL building, query strings, JSON bodies, a
 * per-call timeout, and failures as `ApiError`. What differs per app — credentials, the headers
 * its requests carry, an in-page transport — is passed in as options.
 */

export interface ApiRequestInit extends Omit<RequestInit, 'body'> {
  /** Serialised as JSON with the matching content type. */
  json?: unknown;
  /** Appended to the URL; `undefined` and `null` values are left out. */
  query?: Record<string, string | number | undefined | null>;
  /**
   * This call's budget in ms, when the client's default is wrong for it (document rendering,
   * email dispatch). 0 disables the timeout.
   */
  timeoutMs?: number;
}

/** Answers a request in-page instead of over the network. */
export type ApiTransport = (url: string, init: RequestInit) => Promise<Response>;

export interface ApiClientOptions {
  /** The API's base without the version: `/api` (same-origin) or `http://localhost:3001`. */
  apiBaseUrl: string;
  /** `v1`. */
  apiVersion: string;
  /** Default budget per request, ms. */
  timeoutMs: number;
  /** Default `credentials`; a call may override it. */
  credentials: RequestCredentials;
  /**
   * Headers for requests to our own API (activity ids, tenant site …), computed per request and
   * applied in order. Never sent elsewhere: a custom header on a cross-origin request forces a
   * CORS preflight.
   */
  headers?: () => Record<string, string>;
  /** An in-page transport to use instead of `fetch`, read per request; null means `fetch`. */
  transport?: () => ApiTransport | null;
}

export interface ApiClient {
  /** `{apiBaseUrl}/{apiVersion}{path}`. */
  url(path: string): string;
  /** Call the API and return the raw (not unwrapped) JSON body; null for an empty body. */
  json<T = unknown>(path: string, init?: ApiRequestInit): Promise<T>;
  /** Call the API and return the body as a Blob (generated PDFs). */
  blob(path: string, init?: ApiRequestInit): Promise<Blob>;
  /**
   * As `json`, with the response headers: paginated endpoints send only the records in the body
   * and the totals in headers (see `readPage`).
   */
  withHeaders<T = unknown>(
    path: string,
    init?: ApiRequestInit,
  ): Promise<{ body: T; headers: Headers }>;
}

/** `{apiBaseUrl}/{apiVersion}{path}`. */
export function buildApiUrl(apiBaseUrl: string, apiVersion: string, path: string): string {
  return `${apiBaseUrl}/${apiVersion}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Whether a URL is our own API. Relative URLs (staging/prod use `/api`) are same-origin by
 * definition; an absolute one must start with the API base.
 */
export function isOwnApiUrl(url: string, apiBaseUrl: string): boolean {
  if (!/^https?:\/\//i.test(url)) {
    return true;
  }
  return url.startsWith(apiBaseUrl);
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const url = (path: string) => buildApiUrl(options.apiBaseUrl, options.apiVersion, path);

  async function request(path: string, init: ApiRequestInit = {}): Promise<Response> {
    const { json, query, headers, timeoutMs = options.timeoutMs, signal, ...rest } = init;

    let target = url(path);
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query ?? {})) {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    }
    if ([...params].length > 0) {
      target += `${target.includes('?') ? '&' : '?'}${params}`;
    }

    const merged = new Headers(headers);
    if (json !== undefined) {
      merged.set('Content-Type', 'application/json');
    }
    if (options.headers && isOwnApiUrl(target, options.apiBaseUrl)) {
      for (const [name, value] of Object.entries(options.headers())) {
        merged.set(name, value);
      }
    }

    // Every request has a budget: one that never answers would otherwise leave the screen
    // waiting forever. A timeout aborts the request and fails as status 0.
    const controller = new AbortController();
    let timedOut = false;
    const timer =
      timeoutMs > 0
        ? setTimeout(() => {
            timedOut = true;
            controller.abort();
          }, timeoutMs)
        : null;
    const onCallerAbort = () => controller.abort();
    signal?.addEventListener('abort', onCallerAbort, { once: true });

    let response: Response;
    try {
      const transport = options.transport?.() ?? null;
      const send: ApiTransport = (input, requestInit) =>
        transport ? transport(input, requestInit) : fetch(input, requestInit);
      response = await send(target, {
        credentials: options.credentials,
        ...rest,
        headers: merged,
        signal: controller.signal,
        ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
      });
    } catch (error) {
      throw new ApiError(
        0,
        null,
        timedOut
          ? `${init.method ?? 'GET'} ${path} exceeded ${timeoutMs} ms`
          : error instanceof Error
            ? error.message
            : 'Network request failed',
      );
    } finally {
      if (timer) {
        clearTimeout(timer);
      }
      signal?.removeEventListener('abort', onCallerAbort);
    }

    if (!response.ok) {
      let body: unknown = null;
      try {
        body = await response.json();
      } catch {
        // Not JSON — the status is what matters.
      }
      throw new ApiError(
        response.status,
        body,
        `${init.method ?? 'GET'} ${path} failed with ${response.status}`,
      );
    }
    return response;
  }

  async function readJson<T>(response: Response): Promise<T> {
    if (response.status === 204) {
      return null as T;
    }
    const text = await response.text();
    return (text ? JSON.parse(text) : null) as T;
  }

  return {
    url,
    async json<T>(path: string, init?: ApiRequestInit): Promise<T> {
      return readJson<T>(await request(path, init));
    },
    async withHeaders<T>(path: string, init?: ApiRequestInit) {
      const response = await request(path, init);
      return { body: await readJson<T>(response), headers: response.headers };
    },
    async blob(path: string, init?: ApiRequestInit): Promise<Blob> {
      return (await request(path, init)).blob();
    },
  };
}
